/**
 * Cloe Ortega – Cart drawer upsell ("You may also like").
 *
 * Reads the product IDs in the bag from `data-product-ids`, asks Shopify's
 * Product Recommendations API for complementary/related products, removes
 * anything already in the bag and renders a swipeable carousel with a size
 * picker and a one-tap "Add" button. Falls back to products from the same
 * collection (rendered by Liquid in #CartUpsellFallback) when Shopify has no
 * recommendations yet.
 *
 * The host element carries `data-skip-subtree-update`, so the theme's morph
 * keeps our rendered children and only updates attributes. When the cart
 * changes, `data-product-ids` changes and we refresh.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const MAX_SOURCES = 3;
const MAX_ITEMS = 8;
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** @type {Map<string, Promise<any[]>>} */
const recCache = new Map();

function sizeImage(src, width = 400) {
  if (!src) return null;
  if (typeof src === 'object') src = src.src || src.url;
  if (!src) return null;
  if (src.startsWith('//')) src = 'https:' + src;
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
}

function optionNames(options) {
  if (!Array.isArray(options)) return [];
  return options.map((o) => (typeof o === 'string' ? o : o && o.name)).filter(Boolean);
}

function normalizeAjaxProduct(p) {
  return {
    id: p.id,
    title: p.title,
    url: p.url || `${ROOT}products/${p.handle}`,
    image: sizeImage(p.featured_image || (p.images && p.images[0])),
    price: p.price,
    compare_at_price: p.compare_at_price || 0,
    options: optionNames(p.options),
    variants: (p.variants || []).map((v) => ({
      id: v.id,
      title: v.title,
      available: v.available,
      price: v.price,
      compare_at_price: v.compare_at_price || 0,
    })),
  };
}

function fetchRecs(productId, intent) {
  const key = `${productId}:${intent}`;
  if (!recCache.has(key)) {
    const url = `${ROOT}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=${intent}`;
    const request = fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((d) => (d.products || []).map(normalizeAjaxProduct))
      .catch(() => []);
    recCache.set(key, request);
  }
  return /** @type {Promise<any[]>} */ (recCache.get(key));
}

function readFallback() {
  const el = document.getElementById('CartUpsellFallback');
  if (!el) return [];
  try {
    const data = JSON.parse(el.textContent || '[]');
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') node.className = v;
    else if (k === 'text') node.textContent = v;
    else if (k === 'html') node.innerHTML = v;
    else node.setAttribute(k, v === true ? '' : String(v));
  }
  for (const child of [].concat(children)) {
    if (child) node.append(child);
  }
  return node;
}

const ICON_ARROW =
  '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 4l6 6-6 6"/></svg>';
const ICON_PLUS =
  '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M10 4v12M4 10h12"/></svg>';
const ICON_CHECK =
  '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10.5l4 4 8-9"/></svg>';

class CartUpsell extends HTMLElement {
  static observedAttributes = ['data-product-ids'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  #requestId = 0;
  #lastKey = '';

  connectedCallback() {
    this.#schedule();
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) this.#schedule();
  }

  get #money() {
    const currency = this.dataset.currency || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency });
  }

  #format(cents) {
    return this.#money.format((Number(cents) || 0) / 100);
  }

  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#load(), 40);
  }

  async #load() {
    const ids = (this.dataset.productIds || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const key = ids.join(',');
    if (key === this.#lastKey && this.childElementCount) return;
    this.#lastKey = key;

    const requestId = ++this.#requestId;

    if (!ids.length) {
      this.replaceChildren();
      return;
    }

    if (!this.childElementCount) this.#renderSkeleton();

    const sources = ids.slice(0, MAX_SOURCES);
    const lists = await Promise.all([
      fetchRecs(sources[0], 'complementary'),
      ...sources.map((id) => fetchRecs(id, 'related')),
    ]);
    if (requestId !== this.#requestId) return;

    const inCart = new Set(ids);
    const seen = new Set();
    const picked = [];
    const accept = (product) => {
      if (!product || picked.length >= MAX_ITEMS) return;
      const id = String(product.id);
      if (inCart.has(id) || seen.has(id)) return;
      if (!product.variants || !product.variants.some((v) => v.available)) return;
      seen.add(id);
      picked.push(product);
    };

    // Interleave recommendations from each bag item so every item influences the list.
    const longest = Math.max(0, ...lists.map((l) => l.length));
    for (let i = 0; i < longest; i++) for (const list of lists) accept(list[i]);
    // Top up with same-collection products if Shopify returned too few.
    if (picked.length < 4) readFallback().forEach(accept);

    if (!picked.length) {
      this.replaceChildren();
      return;
    }
    this.#render(picked);
  }

  #renderSkeleton() {
    const track = el('div', { class: 'cart-upsell__track' });
    for (let i = 0; i < 3; i++) {
      track.append(
        el('div', { class: 'cart-upsell__card cart-upsell__card--skeleton', 'aria-hidden': 'true' }, [
          el('div', { class: 'cart-upsell__media' }),
          el('div', { class: 'cart-upsell__line' }),
          el('div', { class: 'cart-upsell__line cart-upsell__line--short' }),
        ])
      );
    }
    this.replaceChildren(
      el('div', { class: 'cart-upsell__inner is-loading' }, [this.#header(false), track])
    );
  }

  #header(withNav) {
    const nav = withNav
      ? el('div', { class: 'cart-upsell__nav' }, [
          el('button', {
            type: 'button',
            class: 'cart-upsell__arrow cart-upsell__arrow--prev',
            'aria-label': 'Previous products',
            'data-dir': '-1',
            html: ICON_ARROW,
          }),
          el('button', {
            type: 'button',
            class: 'cart-upsell__arrow',
            'aria-label': 'Next products',
            'data-dir': '1',
            html: ICON_ARROW,
          }),
        ])
      : null;

    return el('div', { class: 'cart-upsell__header' }, [
      el('div', {}, [
        el('p', { class: 'cart-upsell__eyebrow', text: this.dataset.eyebrow || 'Picked for you' }),
        el('h3', { class: 'cart-upsell__title', id: 'CartUpsellTitle', text: this.dataset.heading || 'You may also like' }),
      ]),
      nav,
    ]);
  }

  #render(products) {
    const track = el('ul', { class: 'cart-upsell__track', role: 'list', 'aria-labelledby': 'CartUpsellTitle' });
    products.forEach((p) => track.append(this.#card(p)));

    const inner = el('div', { class: 'cart-upsell__inner' }, [this.#header(products.length > 2), track]);
    this.replaceChildren(inner);

    inner.querySelectorAll('.cart-upsell__arrow').forEach((btn) => {
      btn.addEventListener('click', () => {
        const dir = Number(btn.getAttribute('data-dir')) || 1;
        track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
      });
    });

    const updateArrows = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      const prev = inner.querySelector('.cart-upsell__arrow--prev');
      const next = inner.querySelector('.cart-upsell__arrow:not(.cart-upsell__arrow--prev)');
      if (prev) prev.toggleAttribute('disabled', track.scrollLeft <= 2);
      if (next) next.toggleAttribute('disabled', track.scrollLeft >= max);
    };
    track.addEventListener('scroll', updateArrows, { passive: true });
    requestAnimationFrame(updateArrows);
  }

  #card(product) {
    const variants = product.variants || [];
    const single = variants.length === 1;
    const firstAvailable = variants.find((v) => v.available) || variants[0];
    const names = product.options || [];
    const sizeName = names.find((n) => /size|talla/i.test(n)) || names[0] || 'option';
    const placeholder = /size|talla/i.test(sizeName) ? 'Select size' : `Select ${sizeName.toLowerCase()}`;

    const priceNow = el('span', { class: 'cart-upsell__price-now' });
    const priceWas = el('s', { class: 'cart-upsell__price-was' });
    const badge = el('span', { class: 'cart-upsell__badge', hidden: true });

    const setPrice = (variant) => {
      const price = variant ? variant.price : product.price;
      const compare = (variant && variant.compare_at_price) || product.compare_at_price || 0;
      priceNow.textContent = this.#format(price);
      if (compare > price) {
        priceWas.textContent = this.#format(compare);
        priceWas.hidden = false;
        badge.textContent = `-${Math.round(((compare - price) / compare) * 100)}%`;
        badge.hidden = false;
      } else {
        priceWas.hidden = true;
        badge.hidden = true;
      }
    };
    setPrice(single ? firstAvailable : null);

    const media = el('a', { class: 'cart-upsell__media', href: product.url, tabindex: '-1', 'aria-hidden': 'true' }, [
      product.image
        ? el('img', { src: product.image, alt: '', loading: 'lazy', width: '400', height: '400' })
        : null,
      badge,
    ]);

    let select = null;
    if (!single) {
      select = el('select', { class: 'cart-upsell__select', 'aria-label': `${placeholder} for ${product.title}` }, [
        el('option', { value: '', disabled: true, selected: true, text: placeholder }),
        ...variants.map((v) =>
          el('option', {
            value: v.id,
            disabled: !v.available,
            text: v.available ? v.title : `${v.title} – sold out`,
          })
        ),
      ]);
      select.addEventListener('change', () => {
        select.classList.remove('is-invalid');
        setPrice(variants.find((v) => String(v.id) === select.value));
      });
    }

    const button = el('button', {
      type: 'button',
      class: 'cart-upsell__add',
      html: `${ICON_PLUS}<span>Add</span>`,
      'aria-label': `Add ${product.title} to bag`,
    });
    button.addEventListener('click', () => {
      const variantId = single ? firstAvailable && firstAvailable.id : select && select.value;
      if (!variantId) {
        if (select) {
          select.classList.remove('is-invalid');
          void select.offsetWidth;
          select.classList.add('is-invalid');
          select.focus();
          try {
            select.showPicker && select.showPicker();
          } catch {}
        }
        return;
      }
      this.#add(variantId, button);
    });

    return el('li', { class: 'cart-upsell__card' }, [
      media,
      el('div', { class: 'cart-upsell__info' }, [
        el('a', { class: 'cart-upsell__name', href: product.url, text: product.title }),
        el('div', { class: 'cart-upsell__price' }, [priceNow, priceWas]),
      ]),
      el('div', { class: 'cart-upsell__actions' }, [select, button]),
    ]);
  }

  async #add(variantId, button) {
    if (button.disabled) return;
    button.disabled = true;
    button.classList.add('is-loading');

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) sectionIds.add(node.dataset.sectionId);
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const fetchCart = () =>
      fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(
        (r) => r.json()
      );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      const cart = await fetchCart();

      if (data.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'cart-upsell', itemCount: 0 },
        });
        this.dispatchEvent(
          new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' })
        );
        button.classList.remove('is-loading');
        button.classList.add('is-error');
        button.textContent = data.description || 'Unavailable';
        setTimeout(() => {
          button.disabled = false;
          button.classList.remove('is-error');
          button.innerHTML = `${ICON_PLUS}<span>Add</span>`;
        }, 2500);
        return;
      }

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.innerHTML = `${ICON_CHECK}<span>Added</span>`;
      button.closest('.cart-upsell__card')?.classList.add('is-added');

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-upsell',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: (error && error.message) || 'Network error', code: 'SERVICE_UNAVAILABLE' })
      );
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
